import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { black2 } from '~/assets/shared-styles/general';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { DocStatusType } from '~/enums/DocStatusType';
import { MODAL_SIMPLE } from '~/types/modal.types';

import EcModal from '../EcModal/EcModal';
import EcRadioButton from '../EcRadioButton/EcRadioButton';
import EcRadioContainer from '../EcRadioContainer/EcRadioContainer';
import { DocumentStatusLabel } from './DocumentStatusLabel';

const DocumentStatusEditModal = ({
  options,
  selectedValue,
  onSelectedValueChange,
  onSave,
  onHide,
}) => {
  const intl = useIntl();

  return (
    <EcModal
      includeCancelButton={true}
      width="560px"
      modalType={MODAL_SIMPLE}
      title={intl.formatMessage({
        id: 'DocumentStatusEditButton.modalTitle',
        defaultMessage: 'Edit Document Status',
        description: 'Title for document status edit modal',
      })}
      hideModal={onHide}
      modalButtons={
        <Button
          variant="primary"
          text={intl.formatMessage({
            id: 'DocumentStatusEditButton.saveButton',
            defaultMessage: 'Save',
            description: 'Save button text for document status modal',
          })}
          onClick={onSave}
        />
      }
    >
      <EcRadioContainer
        groupName="docStatusGroupOptions"
        onChange={({ selectedValue: value }) => onSelectedValueChange(value)}
        defaultValue={selectedValue}
      >
        {(groupName, selectedVal, onOptionChange) => (
          <div>
            {options.map((opt) => (
              <EcRadioButton
                key={opt.value}
                id={`${opt.label}_Status`}
                groupName="docStatusGroupOptions"
                value={opt.value}
                label={opt.label}
                onChange={onOptionChange}
                selectedValue={selectedVal}
                fontSize="13px"
                labelColor={black2}
              />
            ))}
          </div>
        )}
      </EcRadioContainer>
    </EcModal>
  );
};

const DocumentStatusEditButton = ({ status, handleDocStatusChange }) => {
  const intl = useIntl();
  const [modal, showEditDocStatusModal] = useState(false);
  const [selectedValue, setSelectedValue] = useState(status);

  const DocumentStatusOptions = [
    {
      label: intl.formatMessage({
        id: 'DocumentStatusEditButton.activeStatus',
        defaultMessage: 'Active',
        description: 'Active document status option',
      }),
      value: 'ACTIVE',
    },
    {
      label: intl.formatMessage({
        id: 'DocumentStatusEditButton.inactiveStatus',
        defaultMessage: 'Inactive',
        description: 'Inactive document status option',
      }),
      value: 'INACTIVE',
    },
    {
      label: intl.formatMessage({
        id: 'DocumentStatusEditButton.unknownStatus',
        defaultMessage: 'Unknown',
        description: 'Unknown document status option',
      }),
      value: 'UNKNOWN',
    },
  ];

  useEffect(() => {
    return () => {
      setSelectedValue(status);
    };
  }, [modal]);

  const handleSaveDocumentStatus = () => {
    handleDocStatusChange(selectedValue);
    showEditDocStatusModal(false);
  };

  const isTerminated = status === DocStatusType.Terminated;

  return (
    <Layout ml={2} mr={2}>
      <button
        aria-haspopup={!isTerminated}
        disabled={isTerminated}
        style={{ cursor: isTerminated ? 'default' : 'pointer' }}
        onClick={() => !isTerminated && showEditDocStatusModal(true)}
      >
        <div style={styles.statusHeader}>
          <FormattedMessage
            id="DocumentStatusEditButton.statusHeader"
            defaultMessage="STATUS"
            description="Header text for status section"
          />
        </div>
        <DocumentStatusLabel status={status} />
      </button>
      {modal && (
        <DocumentStatusEditModal
          options={DocumentStatusOptions}
          selectedValue={selectedValue}
          onSelectedValueChange={setSelectedValue}
          onSave={handleSaveDocumentStatus}
          onHide={() => showEditDocStatusModal(false)}
        />
      )}
    </Layout>
  );
};

const styles = {
  statusHeader: {
    marginBottom: '4px',
    display: 'block',
    fontSize: '10px',
    textAlign: 'center',
  },
};

export default DocumentStatusEditButton;
